import { FILTER_LABEL_CLASS } from '@/components/admin/versioned/filter-field';
import type { VersionedCounts, VersionedFilterChip } from '@/lib/versioned-filters';
import { versionedCountLabel } from '@/lib/versioned-filters';
import type { VersionedWording } from '@/lib/versioned-labels';
import type { VersionedFilters } from '@matter_extractor/shared';
import { Icon, Select } from '@pms-digital/design-system';

/**
 * What is armed, how many rows it left, and in which order (refonte modulaire
 * §4).
 *
 * One row, three things, and they belong together: a count without the filters
 * that produced it is a number nobody can question, and a filter one can set
 * without seeing what it cost is a filter one forgets is on. §4 asks for all
 * three by name - « les filtres actifs s'affichent en pastilles retirables ; le
 * compte dit "2 critères sur 6" ; le tri se choisit ».
 *
 * ## A chip removes ITS OWN dimension and this file knows none of them
 *
 * Each chip carries the `patch` that neutralises it, built by
 * `activeVersionedFilters`. So a filter added to an object becomes removable
 * without a line changing here - the drift this shape exists to prevent is a
 * chip that displays and cannot be taken off, which is a dead end an operator
 * escapes by reloading the page.
 *
 * ## The sort is not a filter
 *
 * It has no chip and « Réinitialiser » leaves it alone: it reorders and hides
 * nothing, so an operator who chose « Nom » once did not choose it by accident.
 * Its options come from the object, because each one adds its own to the four
 * of §4.
 *
 * ## A chip is a whole button, so it is not the package `Tag`
 *
 * `Tag` removes through a separate 24 px circle and takes no `data-testid`, while a chip
 * here is ONE button named « Retirer le filtre … » that the suites click by its mark. The
 * chip stays hand-written on the charter's tokens; only its cross is the package `Icon`.
 */

export interface FilterSummaryProps<Filters extends VersionedFilters> {
  wording: VersionedWording;
  chips: readonly VersionedFilterChip<Filters>[];
  counts: VersionedCounts;
  sort: Filters['sort'];
  /** Every sort the object offers, in the order the select lists them. */
  sortOptions: readonly Filters['sort'][];
  /** How each of them is named. */
  sortLabel: Readonly<Record<string, string>>;
  onChange: (patch: Partial<Filters>) => void;
}

export function FilterSummary<Filters extends VersionedFilters>({
  wording,
  chips,
  counts,
  sort,
  sortOptions,
  sortLabel,
  onChange,
}: FilterSummaryProps<Filters>) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      {chips.length === 0 ? (
        <p className="text-gray-500 text-small">Aucun filtre actif</p>
      ) : (
        <div
          data-testid={`${wording.list}-filtres-actifs`}
          className="flex flex-wrap items-center gap-2"
        >
          {chips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              data-testid={`${wording.list}-pastille-${chip.id}`}
              /*
                The accessible name says the GESTURE and repeats the label,
                because the visible text alone (« Tag : en production ») reads
                as a statement rather than as something one can click off.
              */
              aria-label={`Retirer le filtre ${chip.label}`}
              onClick={() => onChange(chip.patch)}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-bleu-nuit-tint py-1 pr-2 pl-2.5 font-semibold text-bleu-nuit text-caption hover:bg-bleu-nuit hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit focus-visible:ring-offset-1"
            >
              <span>{chip.label}</span>
              <Icon name="close" size={14} />
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <p data-testid={`${wording.list}-compte`} className="text-gray-600 text-small">
          {versionedCountLabel(counts, wording.noun)}
        </p>
        {/* « Trier par » in words beside the package `Select` (§9), which keeps it as its name;
            the mark sits on the wrapper, around the combobox. */}
        <div className="flex items-center gap-2">
          <span aria-hidden="true" className={FILTER_LABEL_CLASS}>
            Trier par
          </span>
          <div data-testid={`${wording.list}-tri`} className="w-60 max-w-full">
            <Select
              snug
              label="Trier par"
              hideLabel
              value={sort}
              options={sortOptions.map((option) => ({ value: option, label: sortLabel[option] }))}
              onValueChange={(value) => onChange({ sort: value } as Partial<Filters>)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
